import type { MutationOptions, QueryClient } from '@tanstack/react-query';
import type { NotificationSchedule, UpdateNotificationSchedule } from '@hygge/shared';

export const SCHEDULES_KEY = ['notification-schedules'] as const;
export const SAVE_SCHEDULE_KEY = ['notification-schedules', 'save'] as const;

export interface ScheduleSave {
  key: string;
  body: UpdateNotificationSchedule;
}

/** The cached schedule list with one schedule's save applied, shown before the server answers. [HW-1071] */
export function applyScheduleUpdate(
  list: NotificationSchedule[],
  key: string,
  body: UpdateNotificationSchedule
): NotificationSchedule[] {
  return list.map((s) => (s.key === key ? { ...s, ...body } : s));
}

/**
 * The last schedule request of each client, so the next one starts after it.
 * Not TanStack Query's `scope`: a queued scoped mutation resumes only while
 * the tab has focus (query-core retryer `canContinue`), so a change made just
 * before switching tabs would wait unsent, and be lost if the tab is closed.
 */
const lastRequest = new WeakMap<QueryClient, Promise<unknown>>();

function inOrder<T>(client: QueryClient, send: () => Promise<T>): Promise<T> {
  const previous = lastRequest.get(client) ?? Promise.resolve();
  const next = previous.then(send, send);
  lastRequest.set(
    client,
    next.catch(() => undefined)
  );
  return next;
}

/**
 * The options of a schedule save. [HW-1071]
 *
 * - The change shows at once (onMutate).
 * - Saves go to the server one after another, in the order made: two
 *   lead-day chips clicked quickly, or the digest hour and its switch, can
 *   never land in the wrong order. A queued save still shows at once, and is
 *   sent even if the tab is hidden meanwhile.
 * - Each body is built from what is shown, so a later save carries every
 *   earlier change too.
 * - A refused save reports why; the list is then reloaded from the server,
 *   which is the truth — no local rollback to get wrong.
 * - The list is reloaded once, when the last open save settles: an earlier
 *   reload would show the server state without the saves still queued.
 *   Inside onSettled the settling save still counts as pending (TanStack
 *   Query runs onSettled before it records the result), hence `=== 1`.
 */
export function scheduleSaveOptions(
  client: QueryClient,
  update: (key: string, body: UpdateNotificationSchedule) => Promise<NotificationSchedule>,
  onRefused: (error: unknown) => void
): MutationOptions<NotificationSchedule, unknown, ScheduleSave> {
  return {
    mutationKey: SAVE_SCHEDULE_KEY,
    mutationFn: ({ key, body }) => inOrder(client, () => update(key, body)),
    onMutate: async ({ key, body }) => {
      await client.cancelQueries({ queryKey: SCHEDULES_KEY });
      const list = client.getQueryData<NotificationSchedule[]>(SCHEDULES_KEY);
      if (list) client.setQueryData(SCHEDULES_KEY, applyScheduleUpdate(list, key, body));
    },
    onError: (error) => onRefused(error),
    onSettled: () => {
      if (client.isMutating({ mutationKey: SAVE_SCHEDULE_KEY }) === 1) {
        return client.invalidateQueries({ queryKey: SCHEDULES_KEY });
      }
    },
  };
}
